import React, { useEffect, useMemo, useRef, useState } from 'react';

type BucketSortEntity = {
  key: string;
  label: string;
  order?: number;
};

type BucketSortTaskProps = {
  rowKey: string;
  prompt: string;
  items: BucketSortEntity[];
  buckets: BucketSortEntity[];
  stackMode: boolean;
  requireAllAssigned: boolean;
  dragEnabled?: boolean;
  value: Record<string, string>;
  onChange: (value: Record<string, string>) => void;
  onInputActivity?: () => void;
};

type DragCapabilitySnapshot = {
  hasPointer: boolean;
  hasDraggable: boolean;
  hasTouch: boolean;
  hasMouse: boolean;
};

export const detectBucketSortDragSupport = (
  overrides?: Partial<DragCapabilitySnapshot>
): { supported: boolean; reason: string | null } => {
  const hasPointer =
    typeof overrides?.hasPointer === 'boolean'
      ? overrides.hasPointer
      : typeof window !== 'undefined' && typeof window.PointerEvent !== 'undefined';
  const hasDraggable =
    typeof overrides?.hasDraggable === 'boolean'
      ? overrides.hasDraggable
      : typeof document !== 'undefined' && 'draggable' in document.createElement('div');
  const hasTouch =
    typeof overrides?.hasTouch === 'boolean'
      ? overrides.hasTouch
      : typeof window !== 'undefined' &&
        ('ontouchstart' in window ||
          ((window.navigator && typeof window.navigator.maxTouchPoints === 'number'
            ? window.navigator.maxTouchPoints
            : 0) > 0));
  const hasMouse =
    typeof overrides?.hasMouse === 'boolean'
      ? overrides.hasMouse
      : typeof window !== 'undefined' && 'onmousedown' in window;

  const supported = hasPointer || hasDraggable || hasTouch || hasMouse;
  if (supported) return { supported: true, reason: null };
  return {
    supported: false,
    reason: `pointer=${String(hasPointer)} draggable=${String(hasDraggable)} touch=${String(hasTouch)} mouse=${String(
      hasMouse
    )}`,
  };
};

const sortByOrderOrLabel = (values: BucketSortEntity[]): BucketSortEntity[] => {
  return [...values].sort((a, b) => {
    const aOrder = Number.isFinite(a.order ?? NaN) ? Number(a.order) : Number.MAX_SAFE_INTEGER;
    const bOrder = Number.isFinite(b.order ?? NaN) ? Number(b.order) : Number.MAX_SAFE_INTEGER;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return a.label.localeCompare(b.label);
  });
};

const BucketSortTask: React.FC<BucketSortTaskProps> = ({
  rowKey,
  items,
  buckets,
  stackMode,
  requireAllAssigned,
  dragEnabled = true,
  value,
  onChange,
  onInputActivity,
}) => {
  const showDevModeToggle = import.meta.env.DEV && import.meta.env.VITE_PLAYWRIGHT === '1';
  const sortedItems = useMemo(() => sortByOrderOrLabel(items), [items]);
  const sortedBuckets = useMemo(() => sortByOrderOrLabel(buckets), [buckets]);
  const bucketLabelByKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const bucket of sortedBuckets) {
      map.set(bucket.key, bucket.label);
    }
    return map;
  }, [sortedBuckets]);
  const assignmentEntries = useMemo(() => {
    const map = new Map<string, string>();
    for (const [itemKey, bucketKey] of Object.entries(value || {})) {
      if (!itemKey || !bucketKey) continue;
      map.set(String(itemKey), String(bucketKey));
    }
    return map;
  }, [value]);
  const displayIndexByItemKey = useMemo(() => {
    const map = new Map<string, number>();
    sortedItems.forEach((item, index) => {
      map.set(item.key, index + 1);
    });
    return map;
  }, [sortedItems]);

  const [selectedItemKey, setSelectedItemKey] = useState('');
  const [draggingItemKey, setDraggingItemKey] = useState('');
  const [pointerDragging, setPointerDragging] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [dragAndDropSupported, setDragAndDropSupported] = useState(true);
  const warnedDragUnavailableRef = useRef(false);

  useEffect(() => {
    const result = detectBucketSortDragSupport();
    setDragAndDropSupported(result.supported);
    if (!result.supported && !warnedDragUnavailableRef.current) {
      warnedDragUnavailableRef.current = true;
      if (typeof process !== 'undefined' && process.env.NODE_ENV !== 'production') {
        console.warn(
          `[BucketSortTask:${rowKey}] drag/drop disabled by environment detection (${result.reason || 'unknown'})`
        );
      }
    }
  }, [rowKey]);

  useEffect(() => {
    setManualMode(false);
    setPointerDragging(false);
    setDraggingItemKey('');
    setSelectedItemKey('');
  }, [rowKey]);

  const dragAndDropEnabled = dragEnabled && dragAndDropSupported;
  const showManualFallback = !dragAndDropEnabled || manualMode;

  const assignedCount = sortedItems.reduce((count, item) => (assignmentEntries.has(item.key) ? count + 1 : count), 0);
  const itemCount = sortedItems.length;
  const allAssigned = assignedCount >= itemCount;
  const isMisconfigured = itemCount === 0 || sortedBuckets.length === 0;

  const currentStackItem = sortedItems.find((item) => !assignmentEntries.has(item.key));
  const remainingCount = Math.max(0, itemCount - assignedCount);

  const applyAssignment = (itemKey: string, bucketKey: string) => {
    const current = sortedItems.find((item) => item.key === itemKey);
    if (!current || !bucketKey) return;

    const previousBucketKey = assignmentEntries.get(itemKey) || null;
    const next = { ...(value || {}) } as Record<string, string>;
    next[itemKey] = bucketKey;

    if (previousBucketKey === bucketKey) {
      onInputActivity?.();
      return;
    }

    onChange(next);
    onInputActivity?.();
  };

  const unassign = (itemKey: string) => {
    if (!assignmentEntries.has(itemKey)) return;
    const next = { ...(value || {}) } as Record<string, string>;
    delete next[itemKey];
    const previousBucketKey = assignmentEntries.get(itemKey) || null;
    if (previousBucketKey === null) return;

    onChange(next);
    onInputActivity?.();
  };

  const assignToBucket = (bucketKey: string) => {
    if (!bucketKey) return;
    const candidateItemKey = stackMode ? currentStackItem?.key || selectedItemKey : selectedItemKey;
    if (!candidateItemKey) return;
    applyAssignment(candidateItemKey, bucketKey);
    setSelectedItemKey('');
  };

  const onDragStart = (event: React.DragEvent, itemKey: string) => {
    if (!dragAndDropEnabled) return;
    setSelectedItemKey(itemKey);
    setDraggingItemKey(itemKey);
    event.dataTransfer.setData('text/plain', itemKey);
    event.dataTransfer.effectAllowed = 'move';
  };

  const onDragEnd = () => {
    setDraggingItemKey('');
    setPointerDragging(false);
  };

  const onPointerDragStart = (itemKey: string, event: React.PointerEvent<HTMLButtonElement>) => {
    if (!dragAndDropEnabled) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setSelectedItemKey(itemKey);
    setDraggingItemKey(itemKey);
    setPointerDragging(true);
  };

  const onPointerDragEnd = (event: React.PointerEvent<HTMLButtonElement>) => {
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    setPointerDragging(false);
  };

  const onPointerDragCancel = (event: React.PointerEvent<HTMLButtonElement>) => {
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    setPointerDragging(false);
    setDraggingItemKey('');
  };

  const onDrop = (bucketKey: string, event: React.DragEvent) => {
    if (!dragAndDropEnabled) return;
    event.preventDefault();
    const itemKey = event.dataTransfer.getData('text/plain') || draggingItemKey || selectedItemKey;
    if (!itemKey) return;
    applyAssignment(itemKey, bucketKey);
    setSelectedItemKey('');
    setDraggingItemKey('');
    setPointerDragging(false);
  };

  const onDragOver = (event: React.DragEvent) => {
    if (!dragAndDropEnabled) return;
    event.preventDefault();
  };

  const onBucketPointerUp = (bucketKey: string) => {
    if (!pointerDragging) return;
    const itemKey = draggingItemKey || selectedItemKey;
    if (!itemKey) return;
    applyAssignment(itemKey, bucketKey);
    setSelectedItemKey('');
    setDraggingItemKey('');
    setPointerDragging(false);
  };

  const getAssignedToBucket = (bucketKey: string) =>
    sortedItems.filter((item) => assignmentEntries.get(item.key) === bucketKey);

  if (isMisconfigured) {
    const message =
      itemCount === 0
        ? 'BucketSort misconfigured: no items provided.'
        : 'BucketSort misconfigured: no buckets provided.';
    return (
      <div className="rounded-input border border-warning/40 bg-warning-light p-3">
        <p className="text-sm text-warning-text">{message}</p>
      </div>
    );
  }

  return (
    <div
      data-testid={`bucket-sort-task:${rowKey}`}
      className="space-y-4 pr-1"
    >
      <div className="flex flex-wrap items-center gap-2 text-xs uppercase tracking-[0.14em] text-txt-muted">
        <span>Assigned: {assignedCount}</span>
        <span>/</span>
        <span>{itemCount}</span>
        <span>Remaining: {remainingCount}</span>
        {dragAndDropEnabled && showDevModeToggle ? (
          <button
            type="button"
            onClick={() => setManualMode((prev) => !prev)}
            className="inline-flex rounded border border-line bg-surface-card px-2 py-1 text-xs uppercase tracking-[0.14em] text-txt-secondary transition-colors hover:bg-surface-alt"
          >
            {manualMode ? 'Use drag mode' : 'Use no-drag mode'}
          </button>
        ) : null}
        <span>Items: {itemCount}</span>
      </div>

      {requireAllAssigned && !allAssigned ? <p className="text-xs text-warning-text">Assign every item to continue.</p> : null}

      {stackMode ? (
        <div data-testid={`bucket-stack-section:${rowKey}`} className="space-y-3 rounded-card border-2 border-line bg-surface-page/50 p-4 shadow-sm">
          <p className="text-xs uppercase tracking-[0.14em] text-txt-muted">Card stack</p>
          <p className="text-xs text-txt-muted">{currentStackItem ? `${remainingCount} card(s) remaining` : 'All cards assigned'}</p>
          {currentStackItem ? (
            <button
              type="button"
              draggable={dragAndDropEnabled}
              onDragStart={(event) => onDragStart(event, currentStackItem.key)}
              onDragEnd={onDragEnd}
              onPointerDown={(event) => onPointerDragStart(currentStackItem.key, event)}
              onPointerUp={onPointerDragEnd}
              onPointerCancel={onPointerDragCancel}
              onLostPointerCapture={onPointerDragCancel}
              onClick={() => setSelectedItemKey(currentStackItem.key)}
              className={`w-full md:w-[240px] md:max-w-[240px] min-h-[8.5rem] rounded-card border-2 px-4 py-3 text-left text-sm font-medium shadow-sm ${
                selectedItemKey === currentStackItem.key
                  ? 'border-brand-600/60 bg-brand-50 text-txt-primary'
                  : 'border-line bg-surface-card/90 text-txt-primary'
              } ${dragAndDropEnabled ? (pointerDragging && selectedItemKey === currentStackItem.key ? 'touch-none' : 'touch-pan-y') : ''}`}
            >
              <span className="mb-2 block text-xs uppercase tracking-[0.14em] text-txt-muted">
                Card {displayIndexByItemKey.get(currentStackItem.key) || 1}
              </span>
              {currentStackItem.label}
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="space-y-4">
        {!stackMode ? (
          <div data-testid={`bucket-cards-section:${rowKey}`} className="space-y-2">
          <p className="text-xs uppercase tracking-[0.14em] text-txt-muted">Cards</p>
          {sortedItems.map((item) => (
            <button
              key={`${rowKey}:${item.key}`}
              type="button"
              draggable={dragAndDropEnabled}
              onDragStart={(event) => onDragStart(event, item.key)}
              onDragEnd={onDragEnd}
              onPointerDown={(event) => onPointerDragStart(item.key, event)}
              onPointerUp={onPointerDragEnd}
              onPointerCancel={onPointerDragCancel}
              onLostPointerCapture={onPointerDragCancel}
              onClick={() => setSelectedItemKey(item.key)}
              onDoubleClick={() => unassign(item.key)}
              className={`w-full md:w-[240px] md:max-w-[240px] min-h-[8.5rem] rounded-card border-2 px-4 py-3 text-left text-sm font-medium shadow-sm ${
                selectedItemKey === item.key
                  ? 'border-brand-600/60 bg-brand-50 text-txt-primary'
                  : assignmentEntries.has(item.key)
                  ? 'border-brand-700/70 bg-brand-700 text-txt-inverse'
                  : 'border-line bg-surface-card/80 text-txt-secondary'
              } ${dragAndDropEnabled ? (pointerDragging && selectedItemKey === item.key ? 'touch-none' : 'touch-pan-y') : ''}`}
            >
              <span className="mb-2 block text-xs uppercase tracking-[0.14em] text-txt-muted">
                Card {displayIndexByItemKey.get(item.key) || 1}
              </span>
              {item.label}
              {assignmentEntries.has(item.key)
                ? ` (→ ${bucketLabelByKey.get(assignmentEntries.get(item.key) || '') || assignmentEntries.get(item.key)})`
                : null}
            </button>
          ))}
          </div>
        ) : null}

        <div data-testid={`bucket-buckets-section:${rowKey}`} className="space-y-2">
          <p className="text-xs uppercase tracking-[0.14em] text-txt-muted">Buckets</p>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {sortedBuckets.map((bucket) => (
              <div
                key={`${rowKey}:${bucket.key}`}
                data-testid={`bucket-dropzone:${rowKey}:${bucket.key}`}
                className={`min-h-[13.75rem] rounded-card border-2 p-4 shadow-sm ${
                  selectedItemKey || draggingItemKey
                    ? 'border-brand-500/70 bg-brand-50'
                    : 'border-line bg-surface-page/50'
                }`}
                onDragOver={onDragOver}
                onDrop={(event) => onDrop(bucket.key, event)}
                onPointerUp={() => onBucketPointerUp(bucket.key)}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-semibold text-txt-primary">{bucket.label}</p>
                  {showManualFallback ? (
                    <button
                      type="button"
                      onClick={() => assignToBucket(bucket.key)}
                      className="inline-flex rounded border border-line bg-surface-card px-2 py-1 text-xs uppercase tracking-[0.14em] text-txt-secondary transition-colors hover:bg-surface-alt"
                    >
                      assign selected
                    </button>
                  ) : null}
                </div>
                <div className="mt-2 space-y-1">
                  {getAssignedToBucket(bucket.key).length === 0 ? (
                    <p className="text-xs text-txt-muted">No items yet</p>
                  ) : (
                    getAssignedToBucket(bucket.key).map((item) => (
                      <button
                        key={`${rowKey}:${bucket.key}:${item.key}`}
                        type="button"
                        draggable={dragAndDropEnabled}
                        onDragStart={(event) => onDragStart(event, item.key)}
                        onDragEnd={onDragEnd}
                        onPointerDown={(event) => onPointerDragStart(item.key, event)}
                        onPointerUp={onPointerDragEnd}
                        onPointerCancel={onPointerDragCancel}
                        onLostPointerCapture={onPointerDragCancel}
                        onClick={() => setSelectedItemKey(item.key)}
                        className={`w-full min-h-[2.75rem] rounded-input border border-brand-700/80 bg-brand-700 px-3 py-2 text-left text-sm text-txt-inverse shadow-sm ${
                          dragAndDropEnabled ? (pointerDragging && selectedItemKey === item.key ? 'touch-none' : 'touch-pan-y') : ''
                        }`}
                      >
                        {item.label}
                      </button>
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BucketSortTask;
